import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { normalizeEndpoint } from '../error-tracking/normalize-endpoint';
import { isPageUnloading, watchPageUnload } from '../error-tracking/page-unload';
import { RumService } from './rum.service';

export type HttpStatusClass = 'network' | 'timeout' | '4xx' | '5xx' | 'other';

export const REPORT_THROTTLE_MS = 30_000;
const AUTOMATION_AUDIENCE_PATH = /\/automations\/campaigns\/([^/]+)\/customers/;

export function httpStatusClass(status: number): HttpStatusClass {
  if (status === 0) {
    return 'network';
  }
  if (status === 408 || status === 504) {
    return 'timeout';
  }
  if (status >= 500 && status < 600) {
    return '5xx';
  }
  if (status >= 400 && status < 500) {
    return '4xx';
  }
  return 'other';
}

/**
 * Reports every failed HttpClient request to Datadog RUM as an error, grouped by method + endpoint
 * + status class, so each failing endpoint is one Error Tracking issue. RUM records failed requests
 * only as resources, so this is the only place they become errors.
 */
@Injectable()
export class RumHttpErrorInterceptor implements HttpInterceptor {
  private readonly lastReportedAt = new Map<string, number>();

  constructor(private readonly rum: RumService) {
    watchPageUnload();
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const startedAt = performance.now();
    return next.handle(request).pipe(
      tap({
        error: (error: unknown) => {
          if (error instanceof HttpErrorResponse) {
            this.report(request, error, Math.round(performance.now() - startedAt));
          }
        }
      })
    );
  }

  private report(request: HttpRequest<any>, error: HttpErrorResponse, durationMs: number): void {
    // 401s are the auth redirect flow, not a failure to act on.
    if (error.status === 401) {
      return;
    }
    // 400s are request validation (e.g. a send time already in the past): user input, not an app failure.
    if (error.status === 400) {
      return;
    }
    // Requests the browser cancelled because the page is reloading or navigating away.
    if (error.status === 0 && isPageUnloading()) {
      return;
    }

    const method = request.method;
    const { host, endpoint } = normalizeEndpoint(error.url ?? request.url);
    const statusClass = httpStatusClass(error.status);
    const groupingHash = `${method} ${host}${endpoint} ${statusClass}`;

    const now = Date.now();
    const last = this.lastReportedAt.get(groupingHash);
    if (last !== undefined && now - last < REPORT_THROTTLE_MS) {
      return;
    }
    this.lastReportedAt.set(groupingHash, now);

    const online = globalThis.navigator?.onLine ?? true;
    const severity = statusClass === '5xx' || statusClass === 'timeout' || (statusClass === 'network' && online)
      ? 'error'
      : 'warning';
    const templateId = AUTOMATION_AUDIENCE_PATH.exec(endpoint)?.[1];

    const reported = new Error(`${method} ${endpoint} failed with ${error.status === 0 ? 'no response' : error.status}`);
    reported.name = error.status === 0 ? 'NetworkError' : `HttpError ${error.status}`;
    this.rum.addError(reported, {
      groupingHash,
      severity,
      http: {
        host,
        method,
        endpoint,
        status: error.status,
        statusClass,
        durationMs,
        online
      },
      ...(templateId && { automationAudience: { templateId } })
    });
  }
}
